'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';

export type ConfirmationConfig = {
  readonly cancelLabel?: string;
  readonly confirmationLabel: React.ReactNode;
  readonly confirmationVariant?: 'default' | 'destructive';
  readonly confirmationIcon?: React.ReactNode;
  readonly description: React.ReactNode;
  readonly onCancel?: () => void;
  readonly onConfirmation: () => void | Promise<void>;
  readonly overlayClassName?: string;
  readonly title: React.ReactNode;
};

type ConfirmationContextValue = {
  readonly showConfirmation: (options: ConfirmationConfig) => void;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export function ConfirmationDialogProvider({ children }: { readonly children: React.ReactNode }) {
  const [config, setConfig] = useState<ConfirmationConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const showConfirmation = useCallback((options: ConfirmationConfig) => {
    setConfig(options);
  }, []);

  const contextValue = useMemo(() => ({ showConfirmation }), [showConfirmation]);

  const handleClose = useCallback(() => {
    if (!isLoading) setConfig(null);
  }, [isLoading]);

  const handleCancel = useCallback(() => {
    config?.onCancel?.();
    setConfig(null);
  }, [config]);

  const handleConfirm = useCallback(async () => {
    if (isNil(config)) return;

    const result = config.onConfirmation();
    if (!(result instanceof Promise)) {
      setConfig(null);
      return;
    }

    setIsLoading(true);
    try {
      await result;
      setConfig(null);
    } catch {
      // Keep the dialog open so the user can retry; onConfirmation surfaces its own error feedback.
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      {!isNil(config) && (
        <Dialog
          onOpenChange={(open) => {
            if (!open) handleClose();
          }}
          open
        >
          {/* Sit above MUI modals (z-index 1300) so confirmations triggered from
              within a MUI Dialog (e.g. the note dialog) aren't hidden behind it. */}
          <DialogContent
            className="z-[1400]"
            overlayClassName={cn('z-[1400]', config.overlayClassName)}
            showCloseButton={!isLoading}
            size="sm"
          >
            <DialogHeader>
              <DialogTitle>{config.title}</DialogTitle>
              <DialogDescription>{config.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button disabled={isLoading} onClick={handleCancel} variant="secondary" size="sm">
                {config.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                disabled={isLoading}
                onClick={handleConfirm}
                variant={config.confirmationVariant ?? 'default'}
                size="sm"
                isLoading={isLoading}
                icon={config.confirmationIcon}
              >
                {config.confirmationLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </ConfirmationContext.Provider>
  );
}

export function useConfirmationDialog() {
  const context = useContext(ConfirmationContext);
  if (isNil(context)) {
    throw new Error('useConfirmationDialog must be used within ConfirmationDialogProvider');
  }
  return context.showConfirmation;
}
